import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Avatar } from '../ui/Avatar'
import { Spinner } from '../ui/Spinner'
import { usersApi } from '../../api/users'
import { mediaApi } from '../../api/media'
import { authApi } from '../../api/auth'
import { notificationsApi } from '../../api/notifications'
import { FCM_TOKEN_ID_KEY } from '../../hooks/usePushNotifications'
import { useAuthStore } from '../../stores/authStore'
import { useUiStore } from '../../stores/uiStore'
import { useAccountsStore } from '../../stores/accountsStore'
import { useChatStore } from '../../stores/chatStore'
import { useLocaleStore } from '../../stores/localeStore'
import { Toggle } from '../ui/Toggle'
import { Slider } from '../ui/Slider'
import { PrivacySettings } from './PrivacySettings'
import { DevicesPanel } from './DevicesPanel'
import { StarsPanel } from './StarsPanel'
import { PremiumPanel } from './PremiumPanel'
import { MyBotsPanel } from './MyBotsPanel'
import { MyReportsPanel } from './MyReportsPanel'
import { NotificationSettingsPanel } from './NotificationSettingsPanel'
import { MediaLibraryPanel } from './MediaLibraryPanel'
import { AdsManagerPanel } from './AdsManagerPanel'
import { StickerPackCreator } from './StickerPackCreator'
import { ModerationQueuePanel } from './ModerationQueuePanel'
import type { User } from '../../types'

interface SettingsPanelProps {
  onClose: () => void
}

export function SettingsPanel({ onClose }: SettingsPanelProps) {
  const { logout, setTokens, setUserId } = useAuthStore()
  const { addToast, theme, toggleTheme, wallpaper, setWallpaper, fontSize, setFontSize, soundEnabled, toggleSound, emojiStatus, setEmojiStatus, sendOnEnter, setSendOnEnter } = useUiStore()
  const { accounts, activeAccountId, removeAccount, setActiveAccount } = useAccountsStore()
  const { locale, setLocale } = useLocaleStore()
  const navigate = useNavigate()

  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [form, setForm] = useState({ first_name: '', last_name: '', username: '', bio: '' })
  const [dirty, setDirty] = useState(false)
  const avatarInputRef = useRef<HTMLInputElement>(null)
  const [notifPerm, setNotifPerm] = useState<NotificationPermission>('default')
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [showDevices, setShowDevices] = useState(false)
  const [showStars, setShowStars] = useState(false)
  const [showPremium, setShowPremium] = useState(false)
  const [showMyBots, setShowMyBots] = useState(false)
  const [showMyReports, setShowMyReports] = useState(false)
  const [showNotifSettings, setShowNotifSettings] = useState(false)
  const [showMediaLibrary, setShowMediaLibrary] = useState(false)
  const [showAdsManager, setShowAdsManager] = useState(false)
  const [showStickerCreator, setShowStickerCreator] = useState(false)
  const [showModerationQueue, setShowModerationQueue] = useState(false)

  useEffect(() => {
    if ('Notification' in window) setNotifPerm(Notification.permission)
  }, [])

  const requestNotif = useCallback(async () => {
    const perm = await Notification.requestPermission()
    setNotifPerm(perm)
    // После выдачи разрешения (по жесту) — триггерим регистрацию push-токена
    // без перезагрузки страницы.
    if (perm === 'granted') window.dispatchEvent(new Event('ggram:push-enable'))
  }, [])

  const WALLPAPERS: (string | null)[] = [
    null,
    'linear-gradient(135deg,#1a2a3a,#2d4a6a)',
    'linear-gradient(135deg,#1a3a1a,#2d6a2d)',
    'linear-gradient(135deg,#3a1a1a,#6a2d2d)',
    'linear-gradient(135deg,#2a1a3a,#4a2d6a)',
    'linear-gradient(135deg,#3a3a1a,#6a6a2d)',
  ]

  const ACCENT_COLORS = [
    { name: 'Blue', value: '#5ca5e6' },
    { name: 'Green', value: '#4dca88' },
    { name: 'Purple', value: '#a07ff0' },
    { name: 'Red', value: '#e85c5c' },
    { name: 'Orange', value: '#f0a25a' },
    { name: 'Pink', value: '#e87abf' },
  ]

  const setAccent = useCallback((color: string) => {
    document.documentElement.style.setProperty('--accent', color)
    localStorage.setItem('accent', color)
  }, [])

  // Restore saved accent on mount
  useEffect(() => {
    const savedAccent = localStorage.getItem('accent')
    if (savedAccent) document.documentElement.style.setProperty('--accent', savedAccent)
  }, [])

  useEffect(() => {
    usersApi.getMe()
      .then((u) => {
        setUser(u)
        setForm({
          first_name: u.first_name ?? '',
          last_name: u.last_name ?? '',
          username: u.username ?? '',
          bio: u.bio ?? '',
        })
      })
      .catch(() => addToast('error', 'Не удалось загрузить профиль'))
      .finally(() => setLoading(false))
  }, [addToast])

  const setField = (key: keyof typeof form, value: string) => {
    setForm((f) => ({ ...f, [key]: value }))
    setDirty(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const updated = await usersApi.updateProfile(form)
      setUser(updated)
      setDirty(false)
      addToast('success', 'Профиль сохранён')
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Не удалось сохранить')
    } finally {
      setSaving(false)
    }
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setUploadingAvatar(true)
    try {
      const uploaded = await mediaApi.upload(file)
      const updated = await usersApi.setAvatar(uploaded.id)
      setUser(updated)
      addToast('success', 'Аватар обновлён')
    } catch {
      addToast('error', 'Не удалось загрузить аватар')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleDeleteAvatar = async () => {
    setUploadingAvatar(true)
    try {
      await usersApi.deleteAvatar()
      setUser((u) => u ? { ...u, avatar_url: '' } : u)
      addToast('success', 'Аватар удалён')
    } catch {
      addToast('error', 'Не удалось удалить аватар')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleLogout = async () => {
    const pushTokenId = localStorage.getItem(FCM_TOKEN_ID_KEY)
    try { await authApi.logout() } catch { /* ignore */ }
    if (pushTokenId) {
      try { await notificationsApi.removeToken(pushTokenId) } catch { /* ignore */ }
      localStorage.removeItem(FCM_TOKEN_ID_KEY)
      localStorage.removeItem('fcm_token')
    }
    logout()
    navigate('/login', { replace: true })
  }

  const switchAccount = (accountId: string) => {
    if (accountId === activeAccountId) return
    const acct = accounts.find((a) => a.id === accountId)
    if (!acct) return
    setTokens(acct.accessToken, acct.refreshToken)
    setUserId(acct.id)
    setActiveAccount(acct.id)
    useChatStore.getState().setChats([])
    useChatStore.getState().setActiveChat(null)
    window.location.reload()
  }

  const addAccount = () => {
    navigate('/login', { replace: true })
    onClose()
  }

  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || ''

  if (showPrivacy) {
    return <PrivacySettings onBack={() => setShowPrivacy(false)} />
  }
  if (showDevices) {
    return <DevicesPanel onBack={() => setShowDevices(false)} />
  }

  if (showStars) {
    return <StarsPanel onBack={() => setShowStars(false)} />
  }

  if (showPremium) {
    return <PremiumPanel onBack={() => setShowPremium(false)} />
  }

  if (showMyBots) {
    return <MyBotsPanel onBack={() => setShowMyBots(false)} />
  }

  if (showMyReports) {
    return <MyReportsPanel onBack={() => setShowMyReports(false)} />
  }

  if (showNotifSettings) {
    return <NotificationSettingsPanel onBack={() => setShowNotifSettings(false)} />
  }

  if (showMediaLibrary) {
    return <MediaLibraryPanel onBack={() => setShowMediaLibrary(false)} />
  }

  if (showAdsManager) {
    return <AdsManagerPanel onBack={() => setShowAdsManager(false)} />
  }

  if (showStickerCreator) {
    return <StickerPackCreator onBack={() => setShowStickerCreator(false)} />
  }

  if (showModerationQueue) {
    return <ModerationQueuePanel onBack={() => setShowModerationQueue(false)} />
  }

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-bg-hover transition-colors"
          aria-label="Back"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">Настройки</h2>
        {'Notification' in window && notifPerm !== 'granted' && (
          <button
            onClick={requestNotif}
            title="Enable notifications"
            className="p-1.5 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-bg-hover transition-colors text-base"
          >
            🔔
          </button>
        )}
        <button
          onClick={toggleTheme}
          title="Toggle theme"
          className="p-1.5 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-bg-hover transition-colors text-base"
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>
        {dirty && (
          <span className="text-txt-meta text-xs">Несохранённые изменения</span>
        )}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {/* Profile summary */}
          <div className="flex flex-col items-center py-6 px-4 border-b border-divider">
            {/* Clickable avatar for upload */}
            <button
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="relative group"
              title="Change photo"
            >
              <Avatar
                name={user?.first_name || user?.username || '?'}
                src={user?.avatar_url}
                size={72}
              />
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center
                              opacity-0 group-hover:opacity-100 transition-opacity">
                {uploadingAvatar ? (
                  <span className="text-white text-xs animate-spin">⟳</span>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                )}
              </div>
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarUpload}
            />
            {user?.avatar_url && (
              <button
                onClick={handleDeleteAvatar}
                disabled={uploadingAvatar}
                className="mt-1 text-xs text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
              >
                Удалить фото
              </button>
            )}
            {displayName && (
              <p className="text-txt-primary font-semibold mt-3 text-base text-center">{displayName}</p>
            )}
            {user?.phone && (
              <p className="text-txt-secondary text-sm mt-0.5">{user.phone}</p>
            )}
            {user?.username && (
              <p className="text-txt-meta text-xs mt-0.5">@{user.username}</p>
            )}
            {/* Emoji status */}
            <div className="mt-3 flex items-center gap-2 justify-center flex-wrap">
              {['', '🔥', '⚡', '🎯', '💎', '🌙', '🚀', '🎸', '🌟', '💪', '🎉', '👑', '❤️', '🌈'].map((e) => (
                <button
                  key={e || 'none'}
                  onClick={() => setEmojiStatus(e)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg text-base transition-colors
                    ${emojiStatus === e ? 'bg-accent/20 ring-1 ring-accent' : 'hover:bg-bg-hover'}`}
                  title={e ? `Set status: ${e}` : 'Clear status'}
                >
                  {e || <span className="text-txt-meta text-xs">✕</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Notifications */}
          <div className="px-4 py-3 border-b border-divider">
            <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium mb-2">Уведомления</p>
            <button
              onClick={() => setShowNotifSettings(true)}
              className="w-full flex items-center justify-between py-2 px-3 rounded-xl hover:bg-bg-hover transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">🔔</span>
                <div className="text-left">
                  <p className="text-txt-primary text-sm">Настройки уведомлений</p>
                  <p className="text-txt-meta text-xs">Звук, режим тишины, настройки</p>
                </div>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-txt-meta">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>

          {/* Appearance */}
          <div className="px-4 py-3 border-b border-divider">
            <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium mb-3">Оформление</p>
            <div className="flex gap-2 flex-wrap">
              {ACCENT_COLORS.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setAccent(c.value)}
                  style={{ backgroundColor: c.value }}
                  className="w-7 h-7 rounded-full transition-transform hover:scale-110 active:scale-95"
                  title={c.name}
                />
              ))}
            </div>

            {/* Font size slider */}
            <div className="mt-3 flex items-center gap-3">
              <span className="text-txt-meta text-xs shrink-0">Aa</span>
              <Slider min={12} max={18} step={1} value={fontSize} onChange={setFontSize} className="flex-1" />
              <span className="text-txt-primary text-xs shrink-0 w-8 text-right">{fontSize}px</span>
            </div>

            {/* Send mode toggle */}
            <div className="mt-3 flex items-center justify-between">
              <div>
                <span className="text-txt-secondary text-sm">Отправка по Enter</span>
                <p className="text-txt-meta text-[11px]">{sendOnEnter ? 'Enter — отправить, Shift+Enter — новая строка' : 'Ctrl+Enter — отправить, Enter — новая строка'}</p>
              </div>
              <Toggle checked={sendOnEnter} onChange={setSendOnEnter} />
            </div>

            {/* Notification sound toggle */}
            <div className="mt-3 flex items-center justify-between">
              <span className="text-txt-secondary text-sm">Звук уведомлений</span>
              <Toggle checked={soundEnabled} onChange={() => toggleSound()} />
            </div>

            {/* Language picker */}
            <div className="mt-3 flex items-center justify-between">
              <span className="text-txt-secondary text-sm">Язык</span>
              <div className="flex gap-1">
                {([['en', 'EN 🇬🇧'], ['ru', 'RU 🇷🇺']] as const).map(([l, label]) => (
                  <button
                    key={l}
                    onClick={() => setLocale(l)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors
                      ${locale === l ? 'bg-accent text-white' : 'bg-bg-input text-txt-secondary hover:bg-bg-hover'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Chat Wallpaper */}
          <div className="px-4 py-3 border-b border-divider">
            <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium mb-3">Фон чата</p>
            <div className="flex gap-2 flex-wrap">
              {WALLPAPERS.map((wp, i) => (
                <button
                  key={i}
                  onClick={() => setWallpaper(wp)}
                  style={wp ? { background: wp } : undefined}
                  className={`w-9 h-9 rounded-lg border-2 transition-transform hover:scale-110 active:scale-95 flex items-center justify-center
                    ${wallpaper === wp ? 'border-accent' : 'border-divider'}
                    ${!wp ? 'bg-bg-input' : ''}`}
                  title={wp ? 'Wallpaper' : 'No wallpaper'}
                >
                  {!wp && <span className="text-txt-meta text-xs">✕</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Edit form */}
          <form onSubmit={handleSave} className="p-4 space-y-3">
            <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium">Профиль</p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-txt-meta text-xs mb-1">Имя</label>
                <input
                  type="text"
                  value={form.first_name}
                  onChange={(e) => setField('first_name', e.target.value)}
                  placeholder="Иван"
                  maxLength={64}
                  className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm
                             outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
                />
              </div>
              <div>
                <label className="block text-txt-meta text-xs mb-1">Фамилия</label>
                <input
                  type="text"
                  value={form.last_name}
                  onChange={(e) => setField('last_name', e.target.value)}
                  placeholder="Иванов"
                  maxLength={64}
                  className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm
                             outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
                />
              </div>
            </div>

            <div>
              <label className="block text-txt-meta text-xs mb-1">Username</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-txt-meta text-sm">@</span>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => setField('username', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                  placeholder="username"
                  maxLength={32}
                  className="w-full bg-bg-input text-txt-primary rounded-xl pl-7 pr-3 py-2 text-sm
                             outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
                />
              </div>
            </div>

            <div>
              <label className="block text-txt-meta text-xs mb-1">О себе</label>
              <textarea
                value={form.bio}
                onChange={(e) => setField('bio', e.target.value)}
                placeholder="Расскажите о себе..."
                maxLength={512}
                rows={2}
                className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm
                           outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={saving || !dirty}
              className="w-full bg-accent hover:bg-blue-500 disabled:opacity-50 text-white
                         font-medium rounded-xl py-2.5 transition-colors flex items-center justify-center gap-2"
            >
              {saving ? <Spinner size={16} /> : 'Сохранить'}
            </button>
          </form>

          {/* Danger zone */}
          <div className="px-4 pb-6">
            <div className="border-t border-divider mt-2 mb-4" />
            <button onClick={() => setShowStars(true)}
              className="w-full text-left px-3 py-2.5 rounded-xl text-txt-primary hover:bg-bg-hover
                         transition-colors text-sm font-medium flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-base leading-none">⭐</span>
                Звёзды
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            <button onClick={() => setShowPremium(true)}
              className="w-full text-left px-3 py-2.5 rounded-xl text-txt-primary hover:bg-bg-hover
                         transition-colors text-sm font-medium flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-base leading-none">🌟</span>
                Premium
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            <button onClick={() => setShowMyBots(true)}
              className="w-full text-left px-3 py-2.5 rounded-xl text-txt-primary hover:bg-bg-hover
                         transition-colors text-sm font-medium flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-base leading-none">🤖</span>
                Мои боты
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            <button onClick={() => setShowMediaLibrary(true)}
              className="w-full text-left px-3 py-2.5 rounded-xl text-txt-primary hover:bg-bg-hover
                         transition-colors text-sm font-medium flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-base leading-none">🗂️</span>
                Мои файлы
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            <button onClick={() => setShowPrivacy(true)}
              className="w-full text-left px-3 py-2.5 rounded-xl text-txt-primary hover:bg-bg-hover
                         transition-colors text-sm font-medium flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Конфиденциальность
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            <button onClick={() => setShowDevices(true)}
              className="w-full text-left px-3 py-2.5 rounded-xl text-txt-primary hover:bg-bg-hover
                         transition-colors text-sm font-medium flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
                Устройства
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            <button onClick={() => setShowMyReports(true)}
              className="w-full text-left px-3 py-2.5 rounded-xl text-txt-primary hover:bg-bg-hover
                         transition-colors text-sm font-medium flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-base leading-none">🛡️</span>
                Мои жалобы
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            <button onClick={() => setShowAdsManager(true)}
              className="w-full text-left px-3 py-2.5 rounded-xl text-txt-primary hover:bg-bg-hover
                         transition-colors text-sm font-medium flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-base leading-none">📢</span>
                Менеджер рекламы
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            <button onClick={() => setShowStickerCreator(true)}
              className="w-full text-left px-3 py-2.5 rounded-xl text-txt-primary hover:bg-bg-hover
                         transition-colors text-sm font-medium flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-base leading-none">🎨</span>
                Мои стикер-паки
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            <button onClick={() => setShowModerationQueue(true)}
              className="w-full text-left px-3 py-2.5 rounded-xl text-txt-primary hover:bg-bg-hover
                         transition-colors text-sm font-medium flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-base leading-none">⚖️</span>
                Очередь модерации
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            {/* Accounts switcher */}
            {accounts.length > 0 && (
              <div className="mb-3">
                <p className="text-txt-meta text-xs uppercase tracking-wider font-medium px-1 mb-2">Аккаунты</p>
                {accounts.map((acct) => (
                  <div key={acct.id} className={`flex items-center gap-3 px-3 py-2 rounded-xl mb-0.5 transition-colors
                    ${acct.id === activeAccountId ? 'bg-accent/10' : 'hover:bg-bg-hover'}`}>
                    <button className="flex items-center gap-3 flex-1 min-w-0 text-left" onClick={() => switchAccount(acct.id)}>
                      <Avatar name={acct.displayName} src={acct.avatarUrl} size={32} />
                      <div className="flex-1 min-w-0">
                        <p className="text-txt-primary text-sm font-medium truncate">{acct.displayName}</p>
                        {acct.username && <p className="text-txt-meta text-xs">@{acct.username}</p>}
                      </div>
                      {acct.id === activeAccountId && (
                        <span className="text-accent text-xs shrink-0">активен</span>
                      )}
                    </button>
                    {acct.id !== activeAccountId && (
                      <button
                        onClick={() => removeAccount(acct.id)}
                        className="text-txt-meta hover:text-red-400 text-xs shrink-0"
                        title="Remove account"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addAccount}
                  className="w-full text-left px-3 py-2 text-accent text-sm hover:bg-bg-hover rounded-xl transition-colors flex items-center gap-2"
                >
                  <span className="text-lg leading-none">+</span> Добавить аккаунт
                </button>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="w-full text-left px-3 py-2.5 rounded-xl text-red-400 hover:bg-bg-hover
                         transition-colors text-sm font-medium flex items-center gap-2"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Выйти
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
